<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@lumeratec/utils';
import VideoCallButton from '../VideoCallButton.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ActiveMessagesOptions from 'dashboard/components-next/NewConversation/components/ActiveMessagesOptions.vue';

export default {
  name: 'ReplyBottomPanel',
  components: {
    NextButton,
    FileUpload,
    VideoCallButton,
    ActiveMessagesOptions,
  },
  mixins: [inboxMixin],
  props: {
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    conversationType: {
      type: String,
      default: '',
    },
    showQuotedReplyToggle: {
      type: Boolean,
      default: false,
    },
    quotedReplyEnabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['selectWhatsappTemplate', 'toggleQuotedReply', 'replaceText'],
  setup(props) {
    const {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uiSettings,
      updateUISettings,
    } = useUISettings();

    const uploadRef = ref(false);

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // Skip if editor is disabled (e.g., WhatsApp 24-hour window expired)
          if (props.isEditorDisabled) return;

          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          if (uploadTriggerButton) uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uiSettings,
      updateUISettings,
      uploadRef,
    };
  },
  data() {
    return {
      showMacrosPopover: false,
      showAiSummaryPopover: false,
      showUndoSendPopover: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
    }),
    undoSendDelay() {
      return this.uiSettings?.message_undo_send_delay || 0;
    },
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    showAttachButton() {
      if (this.isEditorDisabled) return false;
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isEditorDisabled) return false;
      if (this.isALineChannel || this.isATiktokChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      if (this.isEditorDisabled) return false;
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      if (this.isOnPrivateNote) {
        return getAllowedFileTypesByChannel();
      }

      let channelType = this.channelType || this.inbox?.channel_type;
      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    showMessageSignatureButton() {
      if (this.isEditorDisabled) return false;
      return !this.isOnPrivateNote;
    },
    contactName() {
      const chat = this.$store.getters.getSelectedChat;
      return chat?.meta?.sender?.name || '';
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    quotedReplyToggleTooltip() {
      return this.quotedReplyEnabled
        ? this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
        : this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP');
    },
    showMacrosButton() {
      if (this.isOnPrivateNote || this.isEditorDisabled) return false;
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.MACROS
      );
    },
    showAiSummaryButton() {
      if (this.isOnPrivateNote || this.isEditorDisabled) return false;
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.LUMERAI_TASKS
      );
    },
  },
  mounted() {
    ActiveStorage.start();
  },
  methods: {
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    toggleMacros() {
      this.showMacrosPopover = !this.showMacrosPopover;
      if (this.showMacrosPopover) {
        this.showAiSummaryPopover = false;
        this.showUndoSendPopover = false;
      }
    },
    toggleAiSummary() {
      this.showAiSummaryPopover = !this.showAiSummaryPopover;
      if (this.showAiSummaryPopover) {
        this.showMacrosPopover = false;
        this.showUndoSendPopover = false;
      }
    },
    toggleUndoSend() {
      this.showUndoSendPopover = !this.showUndoSendPopover;
      if (this.showUndoSendPopover) {
        this.showMacrosPopover = false;
        this.showAiSummaryPopover = false;
      }
    },
    setUndoSendDelay(seconds) {
      this.updateUISettings({ message_undo_send_delay: seconds });
      this.showUndoSendPopover = false;
    },
    closePopovers() {
      this.showMacrosPopover = false;
      this.showAiSummaryPopover = false;
      this.showUndoSendPopover = false;
    },
    handleInsertActiveMessage(text) {
      this.$emit(
        'replaceText',
        (this.message || '') + (this.message ? '\n' : '') + text
      );
    },
    handleAttachActiveMessageFile(fileObj) {
      if (fileObj && fileObj.resource) {
        const mockUploadFile = {
          file: fileObj.resource,
          name: fileObj.resource.name,
          size: fileObj.resource.size,
          type: fileObj.resource.type,
        };
        this.onFileUpload(mockUploadFile);
      }
    },
  },
};
</script>

<template>
  <div
    v-on-clickaway="closePopovers"
    class="flex justify-between p-3 relative"
    :class="wrapClass"
  >
    <!-- Macros and AI popovers moved to ReplyTopPanel -->
    <!-- Undo Send Delay popover -->
    <div v-if="showUndoSendPopover" class="reply-toolbar-popover">
      <div class="reply-toolbar-popover__header">
        <span class="font-semibold text-sm text-n-slate-12">
          {{ $t('CONVERSATION.REPLYBOX.UNDO_SEND_DELAY') }}
        </span>
        <button
          type="button"
          :aria-label="$t('CONVERSATION.REPLYBOX.CLOSE')"
          class="text-n-slate-10 hover:text-n-slate-12"
          @click="toggleUndoSend"
        >
          <span aria-hidden="true" class="i-lucide-x size-4" />
        </button>
      </div>
      <div class="p-2 flex flex-col gap-1">
        <button
          v-for="time in [0, 3, 5, 10]"
          :key="time"
          class="text-left px-2 py-1.5 rounded-md hover:bg-n-slate-2 text-sm text-n-slate-12 flex justify-between"
          :class="{ 'font-semibold bg-n-slate-1': undoSendDelay === time }"
          @click="setUndoSendDelay(time)"
        >
          <span>
            {{
              time === 0
                ? $t('CONVERSATION.REPLYBOX.UNDO_SEND_DISABLED')
                : $t('CONVERSATION.REPLYBOX.UNDO_SEND_SECONDS', {
                    seconds: time,
                  })
            }}
          </span>
          <span
            v-if="undoSendDelay === time"
            class="text-n-brand dark:text-n-brand-dark"
          >
            <span aria-hidden="true" class="i-lucide-check size-4" />
            <span class="sr-only">
              {{ $t('CONVERSATION.REPLYBOX.SELECTED') }}
            </span>
          </span>
        </button>
      </div>
    </div>
    <div class="left-wrap">
      <NextButton
        v-if="!isEditorDisabled"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        :aria-label="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        icon="i-ph-smiley-sticker"
        slate
        faded
        sm
        @click="toggleEmojiPicker"
      />
      <FileUpload
        v-if="showAttachButton"
        ref="uploadRef"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        input-id="conversationAttachment"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="enableDragAndDrop"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
      >
        <NextButton
          v-if="showAttachButton"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          :aria-label="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          icon="i-ph-paperclip"
          slate
          faded
          sm
        />
      </FileUpload>
      <NextButton
        v-if="showAudioRecorderButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :aria-label="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :icon="!isRecordingAudio ? 'i-ph-microphone' : 'i-ph-microphone-slash'"
        slate
        faded
        sm
        @click="toggleAudioRecorder"
      />
      <NextButton
        v-if="showAudioPlayStopButton"
        :icon="audioRecorderPlayStopIcon"
        slate
        faded
        sm
        :label="recordingAudioDurationText"
        @click="toggleAudioRecorderPlayPause"
      />
      <NextButton
        v-if="showMessageSignatureButton"
        v-tooltip.top-end="signatureToggleTooltip"
        icon="i-ph-signature"
        slate
        faded
        sm
        :aria-label="signatureToggleTooltip"
        @click="toggleMessageSignature"
      />
      <NextButton
        v-if="showQuotedReplyToggle"
        v-tooltip.top-end="quotedReplyToggleTooltip"
        icon="i-ph-quotes"
        :variant="quotedReplyEnabled ? 'solid' : 'faded'"
        color="slate"
        sm
        :aria-pressed="quotedReplyEnabled"
        @click="$emit('toggleQuotedReply')"
      />
      <NextButton
        v-if="enableWhatsAppTemplates"
        v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        sm
        @click="$emit('selectWhatsappTemplate')"
      />
      <VideoCallButton
        v-if="
          (isAWebWidgetInbox || isAPIInbox) &&
          !isOnPrivateNote &&
          !isEditorDisabled
        "
        :conversation-id="conversationId"
      />
      <transition name="modal-fade">
        <div
          v-show="uploadRef && uploadRef.dropActive"
          class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="text-2xl break-words text-n-slate-12">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
      <ActiveMessagesOptions
        v-if="
          !isAWebWidgetInbox &&
          !isAnEmailChannel &&
          !isAWhatsAppChannel &&
          !isNote
        "
        :inbox-id="inbox.id"
        :contact-name="contactName"
        @insert-text="handleInsertActiveMessage"
        @attach-file="handleAttachActiveMessageFile"
      />
      <!-- Macros and AI buttons moved to ReplyTopPanel -->
      <!-- Timer Undo Send Button -->
      <NextButton
        v-if="!isNote"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.UNDO_SEND_DELAY')"
        :aria-label="$t('CONVERSATION.REPLYBOX.UNDO_SEND_DELAY')"
        icon="i-ph-clock"
        :variant="showUndoSendPopover ? 'solid' : 'faded'"
        color="slate"
        sm
        :aria-pressed="showUndoSendPopover"
        @click="toggleUndoSend"
      />
      <div
        v-if="!isNote && undoSendDelay > 0"
        class="text-xs font-semibold text-n-slate-10 bg-n-slate-2 px-1 rounded -ml-1"
      >
        {{
          $t('CONVERSATION.REPLYBOX.UNDO_SEND_COMPACT', {
            seconds: undoSendDelay,
          })
        }}
      </div>
    </div>
    <div class="right-wrap">
      <NextButton
        :label="sendButtonText"
        type="submit"
        sm
        :color="isNote ? 'amber' : 'blue'"
        :disabled="isSendDisabled"
        class="flex-shrink-0"
        @click="onSend"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.left-wrap {
  @apply items-center flex gap-2;
}

.right-wrap {
  @apply flex;
}

:deep(.file-uploads) {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply enabled:bg-n-slate-9/20;
  }
}

.reply-toolbar-popover {
  @apply absolute bottom-full left-0 mb-2 z-50
         w-80 max-h-96 overflow-y-auto rounded-xl
         border border-n-slate-3 dark:border-n-slate-6
         bg-n-alpha-white dark:bg-n-solid-2
         shadow-lg;

  &__header {
    @apply flex justify-between items-center
           px-4 py-3
           border-b border-n-slate-3 dark:border-n-slate-6
           sticky top-0
           bg-n-alpha-white dark:bg-n-solid-2;
  }
}
</style>
